import { TextField, Grid, MenuItem, Card, CardHeader, CardContent, Tooltip } from "@mui/material"
import { forwardRef, useImperativeHandle, memo } from "react"

import { useFormik } from "formik"
import * as Yup from "yup"
import { useStores } from "stores/root-store"
import { rootStore } from "stores/root-store"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { CommonConstant } from "shared/constants/common-constant"

const InboundDeliveryInfo = forwardRef(({ deliveryInfo, onErpOrderChange }, ref) => {
  const { InboundDeliveryStore } = useStores()
  const isEditing = Boolean(InboundDeliveryStore.deliveryInfo)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: deliveryInfo,
    validationSchema: Yup.object({
      receipt_id: Yup.string().max(25).required("Receipt ID is required"),
      scheduled_date: Yup.date().required("Scheduled Date is required"),
      warehouse: Yup.string().required("Warehouse is required"),
      receipt_type: Yup.string().required("Receipt Type is required"),
    }),
    onSubmit: async values => {
      //console.log(values)
    },
  })

  useImperativeHandle(ref, () => ({
    async getFormDataIfValid() {
      const val = await formik.validateForm()
      await formik.submitForm()

      if (!val || Object.keys(val).length == 0) return formik.values
      return null
    },
  }))

  const handleDateChanged = newValue => {
    if (new Date(newValue).getFullYear() > 1900) formik.setFieldValue("scheduled_date", newValue)
  }

  const handleErpOrderChange = event => {
    onErpOrderChange(event.target.value)
    formik.handleChange(event)
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card>
        <CardHeader title="Delivery Information" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="unique identifier for the inbound delivery">
                <TextField
                  fullWidth
                  error={Boolean(formik.touched.receipt_id && formik.errors.receipt_id)}
                  helperText={formik.touched.receipt_id && formik.errors.receipt_id}
                  label="Receipt ID"
                  name="receipt_id"
                  id="receipt_id"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  required
                  value={formik.values.receipt_id}
                  variant="outlined"
                  inputProps={{ maxLength: 25 }}
                  InputProps={{
                    readOnly: isEditing,
                  }}
                />
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="expected delivery date">
                <DatePicker
                  label="Scheduled Date"
                  value={formik.values.scheduled_date}
                  id="scheduled_date"
                  format={CommonConstant.DATE_FORMAT}
                  onChange={handleDateChanged}
                  renderInput={props => (
                    <TextField
                      fullWidth
                      error={Boolean(formik.touched.scheduled_date && formik.errors.scheduled_date)}
                      helperText={formik.touched.scheduled_date && formik.errors.scheduled_date}
                      {...props}
                    />
                  )}
                />
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="destination warehouse of the inbound delivery">
                <TextField
                  fullWidth
                  error={Boolean(formik.touched.warehouse && formik.errors.warehouse)}
                  helperText={formik.touched.warehouse && formik.errors.warehouse}
                  required
                  select={!isEditing}
                  label="Warehouse"
                  name="warehouse"
                  id="warehouse"
                  onChange={formik.handleChange}
                  value={formik.values.warehouse}
                  variant="outlined"
                  InputProps={{
                    readOnly: isEditing,
                  }}
                >
                  {rootStore.getOptionsByFilter("WAREHOUSE").map(option => (
                    <MenuItem id={`warehouse-${option.value}`} key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="secondary unique identifier - sales order / purchase order / invoice number">
                <TextField
                  fullWidth
                  label="ERP Number"
                  name="erp_order"
                  id="erp_order"
                  onBlur={formik.handleBlur}
                  onChange={handleErpOrderChange}
                  value={formik.values.erp_order}
                  variant="outlined"
                  inputProps={{ maxLength: 25 }}
                />
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="supplier name">
                <TextField
                  fullWidth
                  label="Supplier"
                  name="source"
                  id="source"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.source}
                  variant="outlined"
                  inputProps={{ maxLength: 25 }}
                />
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="inbound tracking number">
                <TextField
                  fullWidth
                  label="Tracking Number"
                  name="trailer_id"
                  id="trailer_id"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.trailer_id}
                  variant="outlined"
                  inputProps={{ maxLength: 25 }}
                />
              </Tooltip>
            </Grid>
            <Grid item md={6} xs={12}>
              <Tooltip placement="top" title="type of full shipping container or less than container load">
                <TextField
                  error={Boolean(formik.touched.receipt_type && formik.errors.receipt_type)}
                  fullWidth
                  helperText={formik.touched.receipt_type && formik.errors.receipt_type}
                  select
                  required
                  label="Receipt Type"
                  name="receipt_type"
                  id="receipt_type"
                  onChange={formik.handleChange}
                  value={formik.values.receipt_type || ""}
                  variant="outlined"
                >
                  {rootStore.getOptionsByFilter("RECEIPT_TYPE").map(option => (
                    <MenuItem id={`receipt_type-${option.value}`} key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  )
})

export default memo(InboundDeliveryInfo)
